import React, { createContext, useContext, useState, useEffect } from "react";
import { createTheme } from "@mui/material";

const StateContext = createContext();

//* DS token mirror — keep in lockstep with src/styles/ds-tokens.css
const DS = {
  light: {
    //! Keep in lockstep with ds-tokens.css light theme (warm off-white palette).
    bg1: "#f7f5f0",
    bg2: "#ffffff",
    fg1: "#1a1d24",
    fg2: "#3c4350",
    divider: "#e6e0d4",
    shadowPaper: "0 1px 2px rgba(15,23,42,.04), 0 1px 3px rgba(15,23,42,.06)",
  },
  dark: {
    //! Keep in lockstep with ds-tokens.css dark theme (neutral graphite palette).
    //! These had drifted: the CSS was re-toned from cool slate to neutral graphite
    //! (ds-tokens.css:229 still notes "neutral text (was cool slate)") and this
    //! mirror was never updated — only the light block was. Since MUI reads its
    //! palette from HERE, every Card/Paper/AppBar in the app painted the OLD blue-grey
    //! (#14171f) right next to Tailwind surfaces painting the NEW neutral (#1a1a1a).
    //! Two darks, side by side, app-wide. Measured live in the browser:
    //!   was bg1 #090b10 / bg2 #14171f / fg1 #f1f5f9 / fg2 #94a3b8  (cool slate)
    bg1: "#0d0d0d",
    bg2: "#1a1a1a",
    fg1: "#f5f5f5",
    fg2: "#a3a3a3",
    divider: "rgba(255,255,255,0.08)",
    shadowPaper: "0 4px 8px rgba(0,0,0,.45), 0 0 0 1px rgba(255,255,255,.05)",
  },
  //! Monochrome primary: dark-slate on light, near-white on dark
  primaryLight: "#141922",
  primaryDark: "#ffffff",
  fontFamily: '"Inter", ui-sans-serif, system-ui, -apple-system, sans-serif',
};

const initialState = {
  chat: false,
  cart: false,
  userProfile: false,
  notification: false,
};

export const ContextProvider = ({ children }) => {
  const [screenSize, setScreenSize] = useState(undefined);
  const [currentColor, setCurrentColor] = useState(localStorage.getItem("colorMode") || "#03C9D7");
  const [currentMode, setCurrentMode] = useState(localStorage.getItem("themeMode") || "Dark");
  const [themeSettings, setThemeSettings] = useState(false);
  const [activeMenu, setActiveMenu] = useState(true);
  const [isClicked, setIsClicked] = useState(initialState);
  const [showSearch, setShowSearch] = useState(false);
  const [PageCat, setPageCat] = useState();
  const [PageName, setPageName] = useState();
  const [StockPrices, setStockPrices] = useState({
    LastPrice: 0,
    AskPrice: 0,
    BitPrice: 0,
  });

  // MUI theme state - synchronized with currentMode
  const [mode, setMUIMode] = React.useState(currentMode.toLowerCase());

  // When currentMode changes, update MUI mode to match
  useEffect(() => {
    setMUIMode(currentMode.toLowerCase());
  }, [currentMode]);

  const MUItheme = React.useMemo(
    () => {
      // --> resolve DS token shard for current mode
      const m = mode === "light" ? "light" : "dark";
      return createTheme({
        palette: {
          mode,
          //! Monochrome primary — dark-slate (#141922) on light, near-white (#ffffff) on dark
          primary: {
            main: m === "light" ? DS.primaryLight : DS.primaryDark,
            contrastText: m === "light" ? "#ffffff" : "#0A0D14",
          },
          // --> palette.accent carries the user-selectable accent (currentColor)
          //     secondary is reserved — used in BrokerPlatforms pages
          accent: {
            main: currentColor,
          },
          background: {
            default: DS[m].bg1,
            paper:   DS[m].bg2,
          },
          text: {
            primary:   DS[m].fg1,
            secondary: DS[m].fg2,
          },
          divider: DS[m].divider,
        },
        typography: {
          fontFamily: DS.fontFamily,
        },
        components: {
          MuiPaper: {
            styleOverrides: {
              root: {
                boxShadow: DS[m].shadowPaper,
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: DS[m].bg2,
                boxShadow: m === "light" ? DS.light.shadowPaper : "none",
              },
            },
          },
        },
      });
    },
    [mode, currentColor]
  );

  const MUIcolorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        const newMode = mode === "light" ? "dark" : "light";
        setMUIMode(newMode);
        setCurrentMode(newMode.charAt(0).toUpperCase() + newMode.slice(1)); // Capitalize first letter
      },
    }),
    [mode]
  );

  //--> Set BOTH the hex (for var()/color-mix use) and the rgb channels (so
  //    Tailwind opacity modifiers like bg-accent/20 work) when the accent changes.
  const applyAccent = (hex) => {
    if (!hex) return;
    const root = document.documentElement.style;
    root.setProperty('--color-accent', hex);
    const h = hex.replace('#', '');
    if (h.length >= 6) {
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      root.setProperty('--color-accent-rgb', `${r} ${g} ${b}`);
    }
  };

  useEffect(() => {
     // Set initial accent color on mount (state is already correct)
     applyAccent(currentColor);
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setMode = (e) => {
    const newMode = e.target.value;
    setCurrentMode(newMode);
    setMUIMode(newMode.toLowerCase()); // Also update MUI mode
    localStorage.setItem("themeMode", newMode);
  };

  const setColor = (color) => {
    setCurrentColor(color);
    localStorage.setItem("colorMode", color);
    // Update CSS variables (hex + rgb channels) for dynamic tailwind usage
    applyAccent(color);
  };

  const handleClick = (clicked) =>
    setIsClicked({ ...initialState, [clicked]: true });

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <StateContext.Provider
      value={{
        currentColor,
        currentMode,
        activeMenu,
        screenSize,
        setScreenSize,
        handleClick,
        isClicked,
        initialState,
        MUItheme,
        MUIcolorMode,
        setIsClicked,
        setActiveMenu,
        setCurrentColor,
        setCurrentMode,
        setMode,
        setColor,
        themeSettings,
        setThemeSettings,
        showSearch,
        setShowSearch,
        PageCat,
        setPageCat,
        PageName,
        setPageName,
        StockPrices,
        setStockPrices,
      }}
    >
      {children}
    </StateContext.Provider>
  );
};

export const useStateContext = () => useContext(StateContext);
